import React, { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useDropzone } from "react-dropzone";
import { useFileUploader, useUpdateFile, useCurrentUser } from "utils/hooks";
import {
  useOrganizationSettings,
  getPatientWordPlural,
} from "utils/hooks/organizations/useOrganizationSettings";
import "./FileUploader.scss";
import {
  Animate,
  Button,
  Icon,
  Input,
  PatientsDropdown,
  Textarea,
  AssignToggle,
} from "components";
import { getFileTypeIconFromUrl } from "pages/AppPages/TherapistPages/Resources/components/Files/utils";
import FileIcon from "assets/icons/paperclip.svg";
import { useTranslation } from "react-i18next";

interface FileUploaderProps {
  showPatients?: boolean;
  defaultSharedWith?: any;
  onSuccess: (f?: any) => void;
  currentFile?: any;
  dropZoneOnly?: boolean;
  error?: string;
  groupId?: string;
  fromBuilder?: boolean;
  showDefaultFileToggle?: boolean;
  privatePatientId?: string;
}

const FileUploader = ({
  showPatients,
  defaultSharedWith,
  onSuccess,
  currentFile,
  dropZoneOnly,
  error,
  groupId,
  fromBuilder,
  showDefaultFileToggle,
  privatePatientId,
}: FileUploaderProps) => {
  const {
    onFileChange,
    onFileUpload,
    onGroupFileUpload,
    selectedFile,
    message,
    isLoading,
  } = useFileUploader();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const { t } = useTranslation(["common"]);
  const { settings: orgSettings } = useOrganizationSettings();

  const { user } = useCurrentUser();

  const isFileOwner = user?.id === currentFile?.addedBy;

  const isFullNameNullable =
    defaultSharedWith?.firstName === null &&
    defaultSharedWith?.lastName === null;

  const formattedDefaultSharedWith = defaultSharedWith
    ? {
        value: defaultSharedWith.uuid,
        label: isFullNameNullable
          ? defaultSharedWith.email
          : `${defaultSharedWith.firstName} ${defaultSharedWith.lastName}`,
      }
    : null;

  const [selectedPatients, setSelectedPatients] = useState<any[] | null>(
    currentFile?.sharedWith ||
      (formattedDefaultSharedWith ? [formattedDefaultSharedWith] : [])
  );
  const [defaultFile, setDefaultFile] = useState(
    currentFile ? currentFile.default : false
  );
  const [shareWithOrg, setShareWithOrg] = useState(
    currentFile ? Boolean(currentFile.shareWithOrganization) : false
  );

  const onPatientSelect = (patients: any[]) => {
    setSelectedPatients(patients);
  };

  const uploadFile = async (data: any) => {
    groupId
      ? await onGroupFileUpload({
          file: null,
          args: {
            title: data.title,
            description: data.description,
            selectedPatients: selectedPatients,
            groupId,
            default: defaultFile,
            shareWithOrganization: shareWithOrg,
            organizationId: user?.activeOrganizationId,
          },
          onSuccess: (fileId) => onSuccess(fileId as string),
        })
      : await onFileUpload({
          file: null,
          args: {
            title: data.title,
            description: data.description,
            selectedPatients: selectedPatients,
            default: defaultFile,
            fromBuilder: fromBuilder ? true : false,
            privatePatientId,
            shareWithOrganization: shareWithOrg,
            organizationId: user?.activeOrganizationId,
          },
          onSuccess: (fileId) => onSuccess(fileId as string),
        });
    setTimeout(() => {
      onFileChange(null);
    }, 2000);
  };

  const onDrop = useCallback(
    (files: File[] | null) => {
      onFileChange(files);
      if (dropZoneOnly && files) {
        groupId
          ? onGroupFileUpload({
              file: files[0],
              args: {
                title: files[0].name,
                description: "",
                selectedPatients: selectedPatients,
                shareWithOrganization: shareWithOrg,
                organizationId: user?.activeOrganizationId,
              },
              onSuccess: (fileId) => onSuccess(fileId as string),
            })
          : onFileUpload({
              file: files[0],
              args: {
                title: files[0].name,
                description: "",
                selectedPatients: selectedPatients,
                fromBuilder: fromBuilder ? true : false,
                privatePatientId,
                shareWithOrganization: shareWithOrg,
                organizationId: user?.activeOrganizationId,
              },
              onSuccess: (fileId) => onSuccess(fileId as string),
            });
      }
    },
    [
      onFileChange,
      dropZoneOnly,
      groupId,
      onGroupFileUpload,
      selectedPatients,
      onFileUpload,
      onSuccess,
      fromBuilder,
      privatePatientId,
      shareWithOrg,
      user?.activeOrganizationId,
    ]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  const { submitUpdateFile, updating, isUpdateSuccessful } = useUpdateFile({
    file: currentFile,
    selectedPatients,
  });

  useEffect(() => {
    if (isUpdateSuccessful) {
      onSuccess();
    }
  }, [isUpdateSuccessful, onSuccess]);

  useEffect(() => {
    if (defaultSharedWith) {
      setSelectedPatients([formattedDefaultSharedWith]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultSharedWith]);

  const onUpdateData = async (data: any) => {
    await submitUpdateFile({
      ...data,
      default: defaultFile,
      shareWithOrganization: shareWithOrg,
      organizationId: user?.activeOrganizationId,
    });
  };

  const buttonText = currentFile
    ? t("resources.edit_file_button")
    : t("resources.upload_file_button");

  return (
    <form
      className="file-uploader-container"
      onSubmit={handleSubmit(currentFile ? onUpdateData : uploadFile)}
    >
      {currentFile ? (
        <div className="selected-file__inner with-mb">
          <div className="file-icon">
            <Icon src={getFileTypeIconFromUrl(currentFile.fileUrl)} />
          </div>
          <div className="selected-file__name">
            {currentFile.fileUrl?.substring(
              currentFile.fileUrl.lastIndexOf("/") + 1
            )}
          </div>
        </div>
      ) : (
        <div
          {...getRootProps()}
          className={`file-dropzone ${
            isDragActive ? "file-dropzone--isActive" : ""
          } ${selectedFile ? "selected-file" : ""} ${
            dropZoneOnly ? "drop-zone-only" : "not-drop-zone-only"
          }`}
        >
          <input {...getInputProps()} />
          {isDragActive ? (
            <>
              <div className="file-uploader-container__icon">
                <Icon src={FileIcon} />
              </div>
              <p className="file-name">Drop your file here ...</p>
            </>
          ) : selectedFile ? (
            isLoading ? (
              <div className="selected-file__inner">
                <div className="file-icon">
                  <Icon src={getFileTypeIconFromUrl(selectedFile.name)} />
                </div>
                <div className="selected-file__name">Uploading...</div>
              </div>
            ) : (
              <div className="selected-file__inner">
                <div className="file-icon">
                  <Icon src={getFileTypeIconFromUrl(selectedFile.name)} />
                </div>
                <div className="selected-file__name">{selectedFile.name}</div>
              </div>
            )
          ) : (
            <>
              <div className="file-uploader-container__icon">
                <Icon src={FileIcon} />
              </div>
              <p className="file-name">
                Drop a file here or click to upload a file
              </p>
            </>
          )}
        </div>
      )}
      {message ? (
        <p className="file-uploader-container__message">
          <small>{message}</small>
        </p>
      ) : (
        <p className="input__error">
          <small>{error}</small>
        </p>
      )}
      {!dropZoneOnly && (
        <>
          <Animate animation="fadeInLeft" delay=".35">
            <Input
              name="title"
              forwardRef={register({
                required: true,
              })}
              error={errors.title && "Title field is required"}
              type="text"
              label="Title"
              placeholder="e.g. What is Cognitive Behavioral Therapy?"
              defaultValue={currentFile?.title}
              extraClass="file-uploader-container__title"
              disabled={currentFile && !isFileOwner}
            />
          </Animate>
          <Animate animation="fadeInLeft" delay=".45">
            <Textarea
              name="description"
              label="Description"
              placeholder={
                "e.g CBT is an evidence-based treatment that can help people with depression, anxiety, panic attacks, hard relationships, and many other problems."
              }
              rows={5}
              maxLength={240}
              forwardRef={register({ required: false })}
              defaultValue={currentFile?.description}
              disabled={currentFile && !isFileOwner}
            />
          </Animate>
          {showDefaultFileToggle && (
            <AssignToggle
              checked={defaultFile}
              label={t("calls_to_action.assign_to_new_patients", {
                patientWordPlural: getPatientWordPlural(orgSettings),
              })}
              onChange={() => setDefaultFile(!defaultFile)}
            />
          )}
          {user?.activeOrganizationId && (
            <AssignToggle
              checked={shareWithOrg}
              label={t("organizations.add_to_practice_resources_label")}
              onChange={() => setShareWithOrg(!shareWithOrg)}
            />
          )}
          {showPatients && (
            <PatientsDropdown
              name="selectedPatients"
              isMulti
              label={t("common_labels.share_with")}
              includeEmail={false}
              placeholder={t("common_labels.choose_patients", {
                patientWordPlural: getPatientWordPlural(orgSettings),
              })}
              defaultPatient={null}
              onChange={onPatientSelect}
              allowSelectAll
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              value={selectedPatients}
            />
          )}
          <Button
            type="submit"
            name={`upload_file_button`}
            value={buttonText}
            fullWidth
            isLoading={isLoading || updating}
            disabled={isLoading || updating}
            extraClass="pill submit-button"
          />
        </>
      )}
    </form>
  );
};

export default FileUploader;
